import { useState, useMemo, useEffect } from 'react';
import { PageHelp } from '@/components/ui/PageHelp';
import { useNavigate } from 'react-router-dom';
import { format, addDays } from 'date-fns';
import { Plus, Filter, X, Check, AlertTriangle } from 'lucide-react';
import { useEmployees, useCreateEmployee } from '@/api/resources';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { ContractHoursInput } from '@/components/ui/ContractHoursInput';
import type { ContractType, EmployeePosition, Skill, Employee } from '@/types';
import { SKILL_LABELS, groupSkillsByFamily } from '@/lib/skills';

// Libellés et familles : voir `@/lib/skills` (source unique).

const POSITION_LABELS: Record<EmployeePosition, string> = {
  PHARMACIEN: 'Pharmacien·ne',
  ETUDIANT_REMPLACANT_PHARMACIEN: 'Étudiant remplaçant pharmacien',
  PREPARATEUR: 'Préparateur·trice',
  ETUDIANT: 'Étudiant',
  MAGASINIER: 'Magasinier·e',
  MENAGE: 'Ménage',
  AUTRE: 'Autre',
};

const contractBadge: Record<ContractType, { c: string; bg: string; label: string; short: string }> = {
  CDI: { c: '#1e3a5f', bg: '#e7ecf4', label: 'CDI', short: 'CDI' },
  CDD: { c: '#0891b2', bg: '#e0f3f7', label: 'CDD', short: 'CDD' },
  REMPLACANT: { c: '#ff6b57', bg: '#ffe8e4', label: 'Remplaçant', short: 'REMP' },
  INTERIM: { c: '#c026d3', bg: '#fae8ff', label: 'Intérim', short: 'INT' },
  APPRENTISSAGE: { c: '#059669', bg: '#d1fae5', label: 'Apprentissage', short: 'APP' },
  STAGE: { c: '#64748b', bg: '#f1f5f9', label: 'Stage', short: 'STA' },
};

export default function EmployeesPage() {
  const { data: employees, isLoading } = useEmployees();
  const [wizardMode, setWizardMode] = useState<'employee' | 'owner' | null>(null);
  const [filter, setFilter] = useState<'all' | 'permanent' | 'temporary' | 'archived'>('all');
  // Liste des archivés chargée séparément (visible uniquement via le filtre « Archivés »).
  const { data: archivedEmployees, isLoading: loadingArchived } = useEmployees({ archived: true });

  const filtered = useMemo(() => {
    if (filter === 'archived') return archivedEmployees ?? [];
    if (!employees) return [];
    if (filter === 'permanent') return employees.filter((e) => e.contractType === 'CDI' || e.contractType === 'CDD');
    if (filter === 'temporary') return employees.filter((e) => e.contractType === 'REMPLACANT' || e.contractType === 'INTERIM');
    return employees;
  }, [employees, archivedEmployees, filter]);

  const isLoadingList = filter === 'archived' ? loadingArchived : isLoading;
  const tempCount = employees?.filter((e) => e.contractType === 'REMPLACANT' || e.contractType === 'INTERIM').length ?? 0;
  const archivedCount = archivedEmployees?.length ?? 0;

  return (
    <div className="flex flex-col h-full">
      {/* Topbar */}
      <div className="border-b border-slate-200 px-8 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-[22px] font-semibold tracking-tight">Salariés <PageHelp topic="employees" /></h1>
          <div className="text-[12px] text-slate-500 mt-0.5">
            {employees?.length ?? 0} actifs · {tempCount} remplaçants/intérim
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" icon={Filter} size="sm">Filtrer</Button>
          <Button variant="default" icon={Plus} size="lg" onClick={() => setWizardMode('owner')}>
            Ajouter un titulaire
          </Button>
          <Button variant="primary" icon={Plus} size="lg" onClick={() => setWizardMode('employee')}>
            Nouveau salarié
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {/* Filtres type */}
        <div className="flex items-center gap-2 mb-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Type</div>
          {(['all', 'permanent', 'temporary', 'archived'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-[11px] rounded-full font-medium border transition-colors ${
                filter === f
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-slate-400'
              }`}
            >
              {f === 'all' ? 'Tous' : f === 'permanent' ? 'Permanents (CDI/CDD)' : f === 'temporary' ? `Remplaçants & intérim (${tempCount})` : `Archivés (${archivedCount})`}
            </button>
          ))}
        </div>

        {/* Bannière CTA */}
        <div className="mb-4 flex items-center justify-between gap-4 bg-gradient-to-r from-primary to-[#2a4a73] rounded-xl p-4 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center">
              <Plus size={20} strokeWidth={2.25} />
            </div>
            <div>
              <div className="text-[14px] font-semibold">Ajouter un nouveau salarié</div>
              <div className="text-[11.5px] text-white/70">
                CDI · CDD · Remplaçant · Intérim — le formulaire s'adapte au type de contrat
              </div>
            </div>
          </div>
          <button
            onClick={() => setWizardMode('employee')}
            className="bg-white text-primary hover:bg-accent-bg font-semibold text-[13px] px-5 py-2.5 rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            <Plus size={14} strokeWidth={2.5} />
            Nouveau salarié
          </button>
        </div>

        {/* Tableau */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="grid grid-cols-[2fr_1fr_1.2fr_1.5fr_0.8fr_40px] px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500 font-medium">
            <div>Salarié</div>
            <div>Contrat</div>
            <div>Poste</div>
            <div>Compétences</div>
            <div className="text-center">Heures</div>
            <div />
          </div>
          {isLoadingList && (
            <div className="px-4 py-8 text-center text-[12px] text-slate-500">Chargement…</div>
          )}
          {!isLoadingList && filtered.length === 0 && (
            <div className="px-4 py-12 text-center">
              <div className="text-[13px] text-slate-500 mb-1">{filter === 'archived' ? 'Aucun salarié archivé' : 'Aucun salarié'}</div>
              <div className="text-[11px] text-slate-400">{filter === 'archived' ? 'Les salariés archivés apparaîtront ici.' : 'Cliquez sur "Nouveau salarié" pour commencer.'}</div>
            </div>
          )}
          {filtered.map((emp, idx) => (
            <EmployeeRow key={emp.id} emp={emp} isEven={idx % 2 === 1} />
          ))}
        </div>
      </div>

      {wizardMode && (
        <NewEmployeeWizard ownerMode={wizardMode === 'owner'} onClose={() => setWizardMode(null)} />
      )}
    </div>
  );
}

function EmployeeRow({ emp, isEven }: { emp: Employee; isEven: boolean }) {
  const navigate = useNavigate();
  const badge = contractBadge[emp.contractType];
  const isTemporary = emp.contractType === 'REMPLACANT' || emp.contractType === 'INTERIM';
  const skills = emp.skills?.map((s) => s.skill) ?? [];

  return (
    <div
      onClick={() => navigate(`/employees/${emp.id}`)}
      className={`grid grid-cols-[2fr_1fr_1.2fr_1.5fr_0.8fr_40px] px-4 py-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 items-center ${
        isEven ? 'bg-slate-50/40' : ''
      }`}
    >
      <div className="flex items-center gap-2.5">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-semibold"
          style={{ backgroundColor: emp.color }}
        >
          {emp.firstName[0]}
          {emp.lastName[0]}
        </div>
        <div>
          <div className="text-[13px] font-medium text-slate-900 flex items-center gap-1.5">
            {emp.firstName} {emp.lastName}
            {emp.archived && <span className="text-[9.5px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600">Archivé</span>}
            {emp.scheduleMode === 'VARIABLE' && (
              <span
                title="Horaires variables : placé automatiquement sur les creux de couverture"
                className="text-[9.5px] font-semibold px-1.5 py-0.5 rounded-full bg-primary-bg text-primary"
              >
                Variable
              </span>
            )}
          </div>
          <div className="text-[10px] text-slate-500">
            {emp.isOwner ? (
              <>Intégré à l'EDT · non décompté</>
            ) : (
              <>
                {isTemporary && emp.endDate && (
                  <>Jusqu'au {new Date(emp.endDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} · </>
                )}
                {emp.contractHours}h hebdo
              </>
            )}
          </div>
        </div>
      </div>
      <div>
        {emp.isOwner ? (
          <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#e7ecf4', color: '#1e3a5f' }}>
            Titulaire
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full"
            style={{ backgroundColor: badge.bg, color: badge.c }}
          >
            {badge.label}
          </span>
        )}
      </div>
      <div className="text-[12px] text-slate-700">{POSITION_LABELS[emp.position]}</div>
      {/* Une pastille par FAMILLE plutôt qu'une par compétence : 3 badges au lieu de 10.
          Le détail reste lisible au survol et sur la fiche salarié. */}
      <div className="flex flex-wrap gap-1">
        {groupSkillsByFamily(skills).map(({ family, skills: owned }) => (
          <span
            key={family.id}
            title={owned.map((s) => SKILL_LABELS[s]).join(' · ')}
            className={`inline-flex items-center gap-1 text-[10.5px] font-medium px-2 py-0.5 rounded-full border ${family.chip}`}
          >
            {family.label}
            <span className="tabular-nums opacity-60">{owned.length}</span>
          </span>
        ))}
        {skills.length === 0 && <span className="text-[11px] text-slate-400 italic">—</span>}
      </div>
      <div className="text-[12px] text-slate-700 text-center tabular-nums">{emp.isOwner ? '—' : `${emp.contractHours}h`}</div>
      <div className="text-right text-slate-300">→</div>
    </div>
  );
}

// ============================================================================
// WIZARD d'embauche
// ============================================================================

function NewEmployeeWizard({ onClose, ownerMode = false }: { onClose: () => void; ownerMode?: boolean }) {
  const createMut = useCreateEmployee();
  const { data: employees } = useEmployees();
  // Un titulaire n'a pas d'étape « type de contrat » → on démarre aux informations.
  const [step, setStep] = useState(ownerMode ? 2 : 1);

  const [contractType, setContractType] = useState<ContractType | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [position, setPosition] = useState<EmployeePosition>('PHARMACIEN');
  const [contractHours, setContractHours] = useState(35);
  // Aujourd'hui par défaut. C'était une date figée (27/04/2026) — et pour un
  // CDI aucun champ ne s'affichait : chaque CDI créé était « embauché » ce
  // jour-là, et ses heures dues partaient d'avril.
  const [startDate, setStartDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(() => format(addDays(new Date(), 7), 'yyyy-MM-dd'));
  const [replacesEmpId, setReplacesEmpId] = useState('');
  const [selectedSkills, setSelectedSkills] = useState<Skill[]>([]);
  // Reprise de l'existant : à l'installation, la pharmacie doit souvent déjà des
  // heures à son équipe. Sans ça on repart d'une feuille blanche.
  const [openingBalance, setOpeningBalance] = useState('');
  const [showLongDurationWarning, setShowLongDurationWarning] = useState(false);

  const durationDays = useMemo(() => {
    if (!startDate || !endDate) return 0;
    return Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24));
  }, [startDate, endDate]);

  useEffect(() => {
    setShowLongDurationWarning(
      (contractType === 'REMPLACANT' || contractType === 'INTERIM') && durationDays > 21
    );
  }, [contractType, durationDays]);

  const replaceableEmployees = employees?.filter((e) => e.contractType === 'CDI') ?? [];

  async function handleSubmit() {
    if (!ownerMode && !contractType) return;
    try {
      await createMut.mutateAsync(
        ownerMode
          ? {
              // Titulaire : pas de contrat/heures/remplacement — juste identité + poste.
              firstName,
              lastName,
              position,
              isOwner: true,
              hireDate: startDate,
              skills: selectedSkills,
            }
          : {
              firstName,
              lastName,
              position,
              contractType: contractType!,
              contractHours,
              hireDate: startDate,
              endDate: contractType !== 'CDI' ? endDate : undefined,
              replacesEmployeeId: replacesEmpId || undefined,
              skills: selectedSkills,
              openingHoursBalance: Number(openingBalance.replace(',', '.')) || 0,
            }
      );
      onClose();
    } catch {
      // erreur via mutation.error
    }
  }

  const contractOptions = [
    { v: 'CDI' as ContractType, l: 'CDI', d: 'Contrat à durée indéterminée', icon: '∞', color: '#1e3a5f' },
    { v: 'CDD' as ContractType, l: 'CDD', d: 'Contrat à durée déterminée', icon: '◷', color: '#0891b2' },
    { v: 'REMPLACANT' as ContractType, l: 'Remplaçant', d: 'Courte durée, souvent à la journée ou semaine', icon: '↺', color: '#ff6b57' },
    { v: 'INTERIM' as ContractType, l: 'Intérim', d: 'Via agence, mission courte', icon: '⚡', color: '#c026d3' },
  ];

  return (
    <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-[640px] max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{ownerMode ? 'Nouveau titulaire' : 'Nouveau salarié'}</div>
            <div className="text-[18px] font-semibold text-slate-900 mt-0.5">
              {ownerMode ? 'Titulaire — informations' : step === 1 ? 'Quel type de contrat ?' : `Embauche — ${contractType ?? ''}`}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X size={16} />
          </button>
        </div>

        {!ownerMode && (
          <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-2 text-[11px]">
            <StepIndicator n={1} label="Type de contrat" active={step === 1} done={step > 1} />
            <div className={`flex-1 h-px ${step > 1 ? 'bg-primary' : 'bg-slate-200'}`} />
            <StepIndicator n={2} label="Informations" active={step === 2} done={false} />
          </div>
        )}
        {ownerMode && (
          <div className="px-6 py-2.5 border-b border-slate-100 bg-primary-bg/40 text-[11.5px] text-slate-600">
            Le titulaire est <strong>intégré à l'emploi du temps</strong> (il comble les trous) mais n'est <strong>pas décompté en heures</strong> et n'apparaît <strong>pas dans la fiche navette</strong>.
          </div>
        )}

        <div className="flex-1 overflow-auto p-6">
          {step === 1 && (
            <div className="space-y-2">
              {contractOptions.map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setContractType(opt.v)}
                  className={`w-full flex items-center gap-4 p-4 border rounded-xl text-left transition-all ${
                    contractType === opt.v
                      ? 'border-primary bg-primary-bg/40 ring-2 ring-primary/10'
                      : 'border-slate-200 hover:border-slate-400'
                  }`}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-[18px] font-semibold flex-shrink-0"
                    style={{ backgroundColor: opt.color + '20', color: opt.color }}
                  >
                    {opt.icon}
                  </div>
                  <div className="flex-1">
                    <div className="text-[14px] font-semibold">{opt.l}</div>
                    <div className="text-[11.5px] text-slate-500 mt-0.5">{opt.d}</div>
                  </div>
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      contractType === opt.v ? 'border-primary' : 'border-slate-300'
                    }`}
                  >
                    {contractType === opt.v && <div className="w-2 h-2 rounded-full bg-primary" />}
                  </div>
                </button>
              ))}
            </div>
          )}

          {step === 2 && (ownerMode || contractType) && (
            <div className="space-y-5">
              {!ownerMode && contractType && (
                <div className="flex items-center gap-2 mb-4">
                  <Chip variant="default" size="md">
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: contractBadge[contractType].c }}
                    />
                    {contractBadge[contractType].label}
                  </Chip>
                  <button
                    onClick={() => setStep(1)}
                    className="text-[11px] text-slate-500 hover:text-slate-900 underline"
                  >
                    Changer
                  </button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Prénom</label>
                  <input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">Nom</label>
                  <input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="input"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="label">Poste</label>
                <select
                  value={position}
                  onChange={(e) => setPosition(e.target.value as EmployeePosition)}
                  className="input"
                >
                  {(Object.keys(POSITION_LABELS) as EmployeePosition[]).map((p) => (
                    <option key={p} value={p}>{POSITION_LABELS[p]}</option>
                  ))}
                </select>
              </div>

              {!ownerMode && contractType === 'CDI' && (
                <div>
                  <label className="label">Date d'embauche</label>
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="input w-48" />
                  <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">
                    Pour un salarié déjà en poste, mettez sa vraie date d'embauche : rien n'est
                    compté avant le <strong>début d'utilisation</strong> du logiciel par l'officine
                    (Paramètres), et le solde de départ ci-dessous reprend ce qui est dû.
                  </p>
                </div>
              )}

              {!ownerMode && contractType !== 'CDI' && (
                <div>
                  <label className="label">Période du contrat</label>
                  <div className="grid grid-cols-2 gap-3">
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="input" />
                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="input" />
                  </div>
                  {durationDays > 0 && (
                    <div className="text-[11px] text-slate-500 mt-2">
                      Durée : <strong>{durationDays} jours</strong> ({Math.round((durationDays / 7) * 10) / 10} semaines)
                    </div>
                  )}
                </div>
              )}

              {showLongDurationWarning && (
                <div className="bg-[#fcf1e2]/60 border border-[#b8741e]/30 rounded-xl p-4 flex items-start gap-3">
                  <AlertTriangle size={16} className="text-[#b8741e] mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-[12.5px] font-semibold mb-1">Durée supérieure à 3 semaines</div>
                    <div className="text-[11.5px] text-slate-700 mb-3">
                      Préférez la sélection <strong>CDD</strong> pour une gestion plus précise des missions et compétences.
                    </div>
                    <div className="flex gap-2">
                      <Button variant="primary" size="sm" onClick={() => setContractType('CDD')}>
                        Passer en CDD →
                      </Button>
                      <Button variant="default" size="sm" onClick={() => setShowLongDurationWarning(false)}>
                        Continuer en {contractType ? contractBadge[contractType].label : ''}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {(contractType === 'REMPLACANT' || contractType === 'INTERIM') && (
                <div>
                  <label className="label">Remplace un salarié ? <span className="text-slate-400 font-normal">(facultatif)</span></label>
                  <select value={replacesEmpId} onChange={(e) => setReplacesEmpId(e.target.value)} className="input">
                    <option value="">— Aucun (horaires libres) —</option>
                    {replaceableEmployees.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.firstName} {e.lastName} ({POSITION_LABELS[e.position]}, {e.contractHours}h)
                      </option>
                    ))}
                  </select>
                  {replacesEmpId && (
                    <div className="mt-3 bg-primary-bg/60 border border-primary/25 rounded-xl p-3 text-[11.5px] text-slate-700">
                      <strong>Horaires et compétences importés automatiquement</strong> depuis le salarié sélectionné.
                    </div>
                  )}
                </div>
              )}

              {(contractType === 'CDI' || contractType === 'CDD') && (
                <div>
                  <label className="label">Heures contractuelles hebdomadaires</label>
                  <ContractHoursInput value={contractHours} onChange={setContractHours} />
                </div>
              )}

              {!ownerMode && (
                <div>
                  <label className="label">Solde d'heures de départ</label>
                  <div className="flex items-center gap-2">
                    <input
                      value={openingBalance}
                      onChange={(e) => setOpeningBalance(e.target.value)}
                      placeholder="0"
                      inputMode="decimal"
                      className="input w-28 text-center tabular-nums"
                    />
                    <span className="text-[12px] text-slate-500">heures</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">
                    Ce que vous lui devez <strong>déjà</strong> aujourd'hui, avant même d'utiliser
                    le logiciel. Mettez un nombre <strong>positif</strong> si vous lui devez des
                    heures, <strong>négatif</strong> s'il vous en doit. Modifiable ensuite depuis
                    <strong> Balance des heures</strong>.
                  </p>
                </div>
              )}

              <div>
                <label className="label">Compétences</label>
                <div className="flex flex-wrap gap-1.5">
                  {(Object.keys(SKILL_LABELS) as Skill[]).map((k) => {
                    const checked = selectedSkills.includes(k);
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() =>
                          setSelectedSkills((s) =>
                            checked ? s.filter((x) => x !== k) : [...s, k]
                          )
                        }
                        className={`text-[11px] px-2.5 py-1 rounded-full border font-medium ${
                          checked
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white border-slate-300 text-slate-700 hover:border-primary'
                        }`}
                      >
                        {SKILL_LABELS[k]}
                      </button>
                    );
                  })}
                </div>
              </div>

              {createMut.error && (
                <div className="bg-danger-bg border border-danger/30 rounded-lg p-3 text-[12px] text-danger">
                  {(createMut.error as Error).message}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50 rounded-b-xl">
          {step === 1 ? (
            <>
              <Button variant="ghost" onClick={onClose}>Annuler</Button>
              <Button variant="primary" onClick={() => contractType && setStep(2)} disabled={!contractType}>
                Continuer →
              </Button>
            </>
          ) : (
            <>
              {ownerMode ? <div /> : <Button variant="ghost" onClick={() => setStep(1)}>← Retour</Button>}
              <div className="flex gap-2">
                <Button variant="default" onClick={onClose}>Annuler</Button>
                <Button variant="primary" icon={Check} loading={createMut.isPending} onClick={handleSubmit}>
                  {ownerMode ? 'Créer le titulaire' : 'Créer le salarié'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StepIndicator({ n, label, active, done }: { n: number; label: string; active: boolean; done: boolean }) {
  return (
    <div className={`flex items-center gap-1.5 ${active || done ? 'text-primary font-medium' : 'text-slate-400'}`}>
      <div
        className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold ${
          done || active ? 'bg-primary text-white' : 'bg-slate-200 text-slate-500'
        }`}
      >
        {done ? <Check size={10} /> : n}
      </div>
      {label}
    </div>
  );
}
